import React, { Component, useRef, useState, useEffect } from 'react';
import {Link, useNavigate, useParams } from "react-router-dom";
import NavBarMini from './NavBarMini.js';
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js'
import { useConfig } from '../contexts/ConfigContext.js';
import { useAdminGuard } from '../hooks/useAdminGuard.js';

import { collection, query, where, getDoc, getDocs, setDoc, updateDoc, doc, Timestamp, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase.js';

const AdminSeasons = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const { isReady } = useAdminGuard();
  const SEASON = config.currentSeason;
  const { season } = useParams();
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [team, setTeam] = useState({})
  const [seasonTeams, setSeasonTeams] = useState({})
  const [currentSeason, setCurrentSeason] = useState({
    conferences: [],
    leaders: [],
    teams: [],
    upcomingGames: []
  })

  useEffect(() => {
    if (!isReady) return;

    const load = async () => {
      if (season) {
        await getCurrentSeasonData();
      }
      await getData();
    };
    load();
  }, [isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCurrentSeasonData = async () => {
    const seasonRef = doc(db, "seasons", season);
    const seasonSnapshot = await getDoc(seasonRef);
    const seasonData = seasonSnapshot.data();

    if (seasonData) {
      setCurrentSeason(seasonData);
    }
  }

  const getData = async () => {
    const seasonTeamsRef = collection(db, "seasonTeams");
    const seasonTeamsSnapshot = await getDocs(seasonTeamsRef);
    const seasonTeamsData = seasonTeamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    setSeasonTeams(seasonTeamsData);

    const teamsRef = collection(db, "teams");
    const teamsSnapshot = await getDocs(teamsRef);
    const teamsData = teamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    setTeams(teamsData);

    const gamesRef = collection(db, "games");
    const gamesSnapshot = await getDocs(gamesRef);
    const gamesData = gamesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date.toDate(),
    }));
    gamesData.sort((a, b) => a.date - b.date);

    setGames(gamesData);
    setDataReady(true);
  }

  function displaySeasons () {
    const seasonsDisplay =  Array.from({ length: Number(SEASON) }).map((_, szn) => displaySeason(szn + 1))

    return seasonsDisplay
  }

  function displaySeason (season) {
    return (
      <div className="large-item-button" onClick={() => navigate(`/admin/seasons/${season}`)}>
        <h2>Season { season }</h2>
      </div>
    )
  }

  const handleConferenceChange = (index, value) => {
    const updated = [...currentSeason.conferences];
    updated[index] = value;

    setCurrentSeason({
      ...currentSeason,
      conferences: updated,
    });
  };

  const addConference = () => {
    const updated = [...(currentSeason.conferences || []), ""];

    setCurrentSeason({
      ...currentSeason,
      conferences: updated,
    });

  };

  function displayEditSeason () {
    let completed = season < SEASON
    let seasonStatus = completed ? 'Completed' : 'Upcoming'

    let teamIDsForSeason = seasonTeams.filter((seasonTeam) => seasonTeam.season == season).map((seasonTeam) => seasonTeam.teamID)

    return (
      <>
        <form>
          <button className="btn btn-dark" type="button" onClick={ editGames } style={{marginRight: '.5rem'}}>Edit Games</button>
          <button className="btn btn-dark" type="button" onClick={ editPlayoffs }>Edit Playoffs</button>

          <div className="form-row">
            <div className="form-group">
              <label for="inputSeasonName">Season Name</label>
              <input type="text" className="form-control" id="inputSeasonName" defaultValue={ season } disabled></input>
            </div>
            <div className="form-group">
              <label for="inputSeasonStatus">Status</label>
              <input type="text" className="form-control" id="inputSeasonStatus" defaultValue={ seasonStatus } disabled></input>
            </div>
          </div>
          <div className="form-group">
            <label>Season Conferences</label>
            {currentSeason.conferences.map((conf, idx) => (
              <input
                key={idx}
                type="text"
                className="form-control"
                style={{ marginBottom: "5px" }}
                id={`inputConference-${idx}`}
                value={conf}
                onChange={(e) => handleConferenceChange(idx, e.target.value)}
              />
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={addConference}
              style={{ marginTop: "5px" }}
              hidden={ completed }
            >
              + Add Conference
            </button>
          </div>
          <div className="form-group">
            <label for="teamsForSeason">Teams for Season</label>
            {
              teams.map((tm) => {
                return (
                  <div className='league-admin-season-team'>
                    <h3><a className='league-link' href={ "/admin/seasons/" + season + "/teams/" + tm.id }>{ tm.name }</a></h3>
                    <input className="form-check-input big-checkbox" type="checkbox" value="" data-team-id={ tm.id } id={ "teamSeasonCheckbox-" + tm.id } defaultChecked={ teamIDsForSeason.includes(tm.id) } disabled={ completed }/>
                  </div>
                )
              })
            }
          </div>
          <button className="btn btn-dark" type="button" onClick={ saveSeason }>Save Season</button>
        </form>
      </>
    )
  }

  async function findOrCreateSeasonTeam (teamID) {
    let team = utils.getTeamForID(teams, teamID)

    let existingSeasonTeam = seasonTeams.find((seasonTeam) => seasonTeam.teamID == teamID && seasonTeam.season == season)

    if (existingSeasonTeam) {
      return existingSeasonTeam
    }

    let seasonTeam = {
      captain: '',
      conference: '',
      players: [],
      season: season,
      seed: '',
      teamID: teamID
    }

    let docRef = await setDoc(doc(db, "seasonTeams", teamID + "-" + season), seasonTeam);

    return seasonTeam
  }

  async function saveSeason() {
    // Collect checked team IDs
    const checkboxes = teams.map((team) =>
      document.getElementById("teamSeasonCheckbox-" + team.id)
    );
    const seasonsTeamIDs = checkboxes
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.dataset.teamId);

    // Make sure seasonTeam docs exist for each selected team
    const newSeasonTeams = await Promise.all(
      seasonsTeamIDs.map((teamID) => findOrCreateSeasonTeam(teamID))
    );

    setSeasonTeams(newSeasonTeams);

    // Build enriched team list (merge seasonTeam data with base team data for ranking)
    const teamsForSeason = newSeasonTeams.map(st => {
      const team = teams.find(t => t.id === st.teamID);
      return { ...st, id: st.teamID, ...team };
    });

    const updatedTeams = utils.getSeasonTeams(games, teamsForSeason, season);

    // Update season object with conferences + merged teams
    const updatedSeason = {
      ...currentSeason,
      conferences: currentSeason.conferences || [],
      teams: updatedTeams,
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, "seasons", season), updatedSeason);
  }


  function editGames () {
    navigate(`/admin/seasons/${season}/games`);
  }

  function editPlayoffs () {
    navigate(`/admin/seasons/${season}/playoffs`);
  }

  if (dataReady == false || !team || team == {}) {
    return (
      <div className="Schedule">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="Schedule">
        <div className="league-content">
          <div className="league-body">
            <p className='league-admin-back-button'><a className='league-link' href="/admin"><b>← Back to Admin Home</b></a></p>
            { season ? <p className='league-admin-back-button'><a className='league-link' href="/admin/seasons"><b>← Back to Admin &gt; Seasons</b></a></p> : '' }

            <p className="league-title-standard-secondary text-align-center">Edit Season</p>
            <div className="league-table-container schedule large-item-buttons-admin">
            {
                season ?
                displayEditSeason()
                :
                displaySeasons()
              }
            </div>
          </div>
        </div>

        <Footer/>
      </div>
    );
  }
}

export default AdminSeasons;
